<script lang="ts" setup>
import { ref } from 'vue'
import type { PropType } from 'vue'
import ImportStatusDisplay from './ImportStatusDisplay.vue'
import PierPassStatusDisplay from './PierPassStatusDisplay.vue'
import ShippingLineImportStatusDisplay from './ShippingLineImportStatusDisplay.vue'
import type { Container } from '~/models/containers'

const props = defineProps({
  container: { type: Object as PropType<Container>, required: true },
})

const selectedImportSource = ref(
  props.container.import_statuses.length > 0
    ? props.container.import_statuses[0].key
    : 'pier_pass'
)
</script>

<template>
  <el-tabs
    v-if="selectedImportSource != null"
    v-model="selectedImportSource"
    class="import-status-details"
  >
    <el-tab-pane
      v-for="importStatus in props.container.import_statuses"
      :key="importStatus.key"
      :name="importStatus.key"
      :label="importStatus.sourceLabel"
    >
      <template #label>
        {{ importStatus.sourceLabel }}
        <ColoredBadge
          v-if="importStatus.isStale"
          v-tooltip="importStatus.staleDescription"
          type="danger"
        >
          Data Fetch Failure
        </ColoredBadge>
      </template>
      <ImportStatusDisplay
        :import-status="importStatus"
        :container="props.container"
      />
    </el-tab-pane>

    <el-tab-pane
      v-if="props.container?.line_import_status"
      :name="props.container.line_import_status.sourceLabel"
      :label="props.container.line_import_status.sourceLabel.toUpperCase()"
    >
      <ShippingLineImportStatusDisplay
        v-if="props.container?.line_import_status"
        :status="props.container.line_import_status"
        :container="props.container"
      />
    </el-tab-pane>

    <el-tab-pane name="pier_pass" label="PierPass">
      <template
        v-if="props.container.pier_pass || props.container.clean_truck_fee"
      >
        <PierPassStatusDisplay
          :pier-pass="props.container.pier_pass ?? undefined"
          :clean-truck-fee="props.container.clean_truck_fee ?? undefined"
          :container="props.container"
        />
      </template>
      <template v-else>
        <div class="pt-2 mb-4">
          <el-descriptions border :column="1" class="flex-1 no-value">
            <el-descriptions-item>
              <template #label>
                We couldn’t load the information. Head over to the
                <el-link
                  href="https://www.pierpass-tmf.org/"
                  target="_blank"
                  rel="noopener"
                  type="primary"
                  underline
                >
                  PierPass portal
                </el-link>
                to check your status.
              </template>
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </template>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" scoped>
.import-status-details .el-descriptions__content {
  word-break: break-all;
}

.no-value :deep(.el-descriptions__content) {
  display: none;
}
</style>
